import React from "react";
import ReactECharts from "echarts-for-react";
import { toTimestamp, formatDateFr } from "../../utils/echartsSeries.js";
import { YEAR_COLORS } from "../../constants.js";

function diffDays(dateA, dateB) {
  return Math.round((new Date(dateA).getTime() - new Date(dateB).getTime()) / 86400000);
}

// Ordre canonique des horizons, que ce soit en mode RCP classique (années)
// ou en mode TRACC (reference/+2/+2.7) — permet de savoir lequel est
// "le plus proche" (référence) et lequel est "le plus sévère" (dernier),
// sans avoir besoin de recevoir le mode en props.
const ORDRE_HORIZON = {
  2020: 0, reference: 0,
  2035: 1, "+2": 1,
  2050: 2, "+2.7": 2,
};

export default function PhenoChart({ data }) {
  const byGroup = new Map();
  data.forEach((row) => {
    if (!byGroup.has(row.YearGroup)) byGroup.set(row.YearGroup, []);
    byGroup.get(row.YearGroup).push(row);
  });

  const groupsOrdonnes = [...byGroup.keys()].sort(
    (a, b) => (ORDRE_HORIZON[a] ?? 99) - (ORDRE_HORIZON[b] ?? 99)
  );
  const horizonBase = groupsOrdonnes[0];
  const horizonDernier = groupsOrdonnes[groupsOrdonnes.length - 1];
  const autresHorizons = groupsOrdonnes.slice(1);

  const series = groupsOrdonnes.map((group) => {
    const rows = byGroup.get(group);
    const sorted = [...rows].sort((a, b) => (a.date_calendaire > b.date_calendaire ? 1 : -1));
    const points = sorted.map((r) => {
      const showLabel = r.is_reco && group === horizonDernier;
      const point = { value: [toTimestamp(r.date_calendaire), r.GDD_VF] };
      if (showLabel) {
        point.label = {
          show: true,
          formatter: r.Libelle_SP,
          position: "left",
          fontWeight: "bold",
        };
      }
      return point;
    });

    return {
      name: String(group),
      type: "line",
      smoothed: true,
      symbol: "circle",
      symbolSize: 8,
      label: { show: false },
      lineStyle: { width: 2.5, color: YEAR_COLORS[group] || "#888" },
      itemStyle: { color: YEAR_COLORS[group] || "#888" },
      data: points,
    };
  });

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", label: { show: false } },
      formatter: (params) => {
        const date = formatDateFr(params[0].value[0]);
        let html = `${date}<br/>`;
        params.forEach((p) => {
          html += `${p.marker} ${p.seriesName}<br/>`;
        });
        return html;
      },
    },
    legend: { bottom: 0, textStyle: { fontSize: 16 } },
    grid: { left: 20, right: 20, top: 20, bottom: 60, containLabel: true },
    xAxis: { type: "time", axisLabel: { formatter: formatDateFr } },
    yAxis: { show: false },
    series,
  };

  // ---- tableau : une ligne par stade "reconnaissable" (is_reco), colonnes
  // horizon de référence (date) / autres horizons (écart en jours vs
  // référence) — les libellés de colonnes s'adaptent au mode (années ou
  // TRACC) selon ce qui est réellement présent dans les données.
  const recoRows = data.filter((r) => r.is_reco);
  const byStage = new Map();
  recoRows.forEach((r) => {
    if (!byStage.has(r.Libelle_SP)) byStage.set(r.Libelle_SP, {});
    byStage.get(r.Libelle_SP)[r.YearGroup] = r.date_calendaire;
  });

  const stagesTries = [...byStage.entries()].sort((a, b) => {
    const da = a[1][horizonBase] ?? Object.values(a[1])[0];
    const db = b[1][horizonBase] ?? Object.values(b[1])[0];
    return new Date(da).getTime() - new Date(db).getTime();
  });

  return (
    <div className="diagoeau-pheno-layout" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div style={{ flex: "2 1 0" }}>
        <ReactECharts option={option} style={{ height: 500 }} notMerge lazyUpdate />
      </div>

      {stagesTries.length > 0 && (
        <div style={{ flex: "1 1 0", overflowX: "auto" }}>
          <table className="diagoeau-data-table diagoeau-table-centered">
            <thead>
              <tr>
                <th></th>
                <th>{String(horizonBase)}</th>
                {autresHorizons.map((h) => (
                  <th key={h}>{String(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stagesTries.map(([label, dates]) => {
                const dBase = dates[horizonBase];
                return (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{dBase ? formatDateFr(dBase) : "—"}</td>
                    {autresHorizons.map((h) => {
                      const d = dates[h];
                      const ecart = dBase && d ? diffDays(d, dBase) : null;
                      return (
                        <td key={h}>
                          {ecart == null ? "—" : `${ecart > 0 ? "+" : ""}${ecart} j`}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}